import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Sparkles, 
  Users, 
  FileCheck, 
  TrendingUp, 
  Building2,
  Receipt,
  FileText,
  Shield,
  BadgeCheck,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';

import { openWhatsApp } from '../utils/whatsappUtils';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleStart = () => {
    openWhatsApp('homepage');
  };

  // Start your business in India - steps (matching reference image)
  const businessSteps = [
    {
      number: '1',
      title: 'Choose your business structure',
      description: 'Private Limited, LLP, or OPC based on your goals & scale.',
    },
    {
      number: '2',
      title: 'We prepare & incorporate',
      description: 'DIN, DSC, name approval, and incorporation documents.',
    },
    {
      number: '3',
      title: 'GST & tax setup',
      description: 'GST registration, PAN, TAN and bank account opening support.',
    },
    {
      number: '4',
      title: 'Ongoing compliance',
      description: 'ROC filings, GST returns, income tax filings.',
    },
  ];

  const steps = [
    { 
      number: '01', 
      title: 'Describe your situation', 
      description: 'Tell us what you need in plain language',
      icon: Sparkles,
      color: 'bg-blue-500'
    },
    { 
      number: '02', 
      title: 'AI maps your needs', 
      description: 'We identify obligations, deadlines & actions',
      icon: FileCheck,
      color: 'bg-violet-500'
    },
    { 
      number: '03', 
      title: 'Experts review & file', 
      description: 'CA/CS professionals ensure accuracy',
      icon: Users,
      color: 'bg-emerald-500'
    },
    { 
      number: '04', 
      title: 'Track everything', 
      description: 'Real-time status and clear timelines',
      icon: Clock,
      color: 'bg-orange-500'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Sticky Navigation */}
      <StickyHeader />

      {/* Hero Section - Split Layout */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Content */}
            <div className="order-2 lg:order-1">
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 mb-6 animate-fade-in-up" data-testid="hero-badges">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200">
                  <BadgeCheck className="h-3.5 w-3.5" />
                  Reviewed by qualified CAs & CS
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                  <Shield className="h-3.5 w-3.5" />
                  Clear pricing
                </span>
              </div>

              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-6 leading-[1.15] text-slate-900 animate-fade-in-up animation-delay-100" data-testid="hero-headline">
                <span className="block">Compliance Powered by AI.</span>
                <span className="block bg-gradient-to-r from-blue-600 via-violet-600 to-emerald-600 bg-clip-text text-transparent">
                  Reviewed by experts.
                </span>
              </h1>

              <p className="text-base md:text-lg lg:text-xl text-slate-600 mb-8 leading-relaxed max-w-xl animate-fade-in-up animation-delay-200">
                Our AI analyses your requirements and maps what's needed.{' '}
                <br className="hidden md:block" />
                Experts handle registrations, filings, and ongoing compliance — so you focus on building, not tracking deadlines.
              </p>

              {/* Single CTA - Intent capture focused */}
              <div className="animate-fade-in-up animation-delay-300">
                <Button 
                  size="lg" 
                  onClick={handleStart}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-base rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all"
                  data-testid="hero-primary-cta"
                >
                  Describe your situation
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Right - Visual (Hidden on mobile for cleaner UX) */}
            <div className="hidden md:block order-1 lg:order-2 relative animate-scale-in animation-delay-200" data-testid="hero-visual">
              <div className="relative">
                {/* Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-200/40 via-violet-200/40 to-emerald-200/40 rounded-3xl blur-3xl transform scale-110 animate-fade-in" />
                
                {/* Main Image Container */}
                <div className="relative bg-gradient-to-br from-white to-slate-50 rounded-3xl p-4 md:p-6 border border-slate-200/60 shadow-2xl shadow-slate-200/50">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a5f9600e-be5f-4116-be7c-136e45582cd1/artifacts/25radu3u_IMG_3168.PNG"
                    alt="Indian startup team collaborating in modern office"
                    className="w-full h-auto rounded-xl object-cover aspect-[4/3]"
                  />
                  
                  {/* Floating Elements */}
                  <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg border border-slate-100 animate-fade-in-up animation-delay-400">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">Expert verified</span>
                    </div>
                  </div>
                  
                  <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg border border-slate-100 animate-fade-in-up animation-delay-500">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">AI-powered</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What do you need help with? - Category Cards */}
      <section className="py-16 bg-white" data-testid="category-section">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              What do you need help with?
            </h2>
            <p className="text-base text-slate-600">
              Select a category to get started
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { name: 'Business Setup', path: '/pricing?category=business-setup', icon: Building2, color: 'bg-violet-100 text-violet-600' },
              { name: 'Licenses', path: '/pricing?category=licenses', icon: FileCheck, color: 'bg-emerald-100 text-emerald-600' },
              { name: 'Trademark', path: '/pricing?category=trademark', icon: Shield, color: 'bg-pink-100 text-pink-600' },
              { name: 'GST', path: '/pricing?category=gst', icon: Receipt, color: 'bg-orange-100 text-orange-600' },
              { name: 'Tax & Payroll', path: '/pricing?category=tax-payroll', icon: FileText, color: 'bg-blue-100 text-blue-600' },
            ].map((category, index) => (
              <Link 
                key={index} 
                to={category.path}
                className="group"
                data-testid={`category-card-${index}`}
              >
                <div className="flex flex-col items-center p-5 bg-white rounded-2xl border border-slate-200/60 hover:border-slate-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-3 ${category.color}`}>
                    <category.icon className="h-6 w-6" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 text-center group-hover:text-slate-900">{category.name}</span>
                </div>
              </Link>
            ))}
          </div>

          {/* View all services CTA - placed below category cards */}
          <div className="text-center mt-8">
            <Link to="/pricing">
              <Button 
                variant="outline" 
                className="rounded-xl border-slate-300 text-slate-600 hover:text-slate-900 hover:border-slate-400"
                data-testid="view-all-services-cta"
              >
                View all services
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Start your business in India - Two Column Layout */}
      <section className="py-16 bg-gradient-to-b from-slate-50/50 to-white" data-testid="start-business-section">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          {/* Section Header */}
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Start your business in India
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl">
              Choose the right entity for your business — Private Limited, LLP, or OPC.
              We handle registrations, tax setup, and ongoing compliance so you can focus on growth.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Step Cards (Vertical Stack) */}
            <div className="space-y-4">
              {businessSteps.map((step, index) => (
                <div 
                  key={index}
                  className="flex gap-4 p-5 bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow duration-300"
                  data-testid={`business-step-${index}`}
                >
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 bg-blue-100 text-blue-600 font-bold text-lg rounded-xl">
                      {step.number}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-slate-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right - Startup India Image (Hidden on mobile) */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative w-full max-w-md">
                {/* Image container - clean, minimal */}
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a5f9600e-be5f-4116-be7c-136e45582cd1/artifacts/13rdat42_IMG_3169.PNG"
                    alt="Indian professionals collaborating on business planning"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    data-testid="startup-india-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How ComplyKit works - Timeline */}
      <section className="py-16 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How COMPLYKIT works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A clear, step-by-step process with full transparency
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative" data-testid={`step-${index}`}>
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-slate-200 to-slate-100" />
                )}
                
                <div className="text-center">
                  <div className={`h-20 w-20 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                    <step.icon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{step.number}</span>
                  <h3 className="text-lg font-semibold text-slate-900 mt-1 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA - Start with clarity */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="footer-cta">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Start with clarity
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-xl mx-auto">
            Tell us what you need. Get a clear plan with timelines and transparent pricing.
          </p>
          <Button 
            size="lg" 
            onClick={handleStart}
            className="bg-white text-slate-900 hover:bg-slate-100 px-10 py-6 text-base rounded-xl shadow-lg hover:shadow-xl transition-all"
            data-testid="footer-cta-button"
          >
            Get Started
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
